import React from 'react';

import { useTranslation } from 'react-i18next';

import { Typography, Box, IconButton } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import { EN, ES } from '@/utils/constants';
import { useWindowDimensions } from '@/hooks/useWindowDimensions';
import LanguageSvg from '@/assets/LanguageSvg.svg';
import Button, { ButtonProps, SelectButton } from '@/components/Button';
import Popover from '@/components/Popover';

type LanguageOptionButtonProps = {
  isSelected: boolean;
} & ButtonProps;

const LanguageOptionButton = ({ isSelected, children, ...props }: LanguageOptionButtonProps) => (
  <SelectButton
    {...props}
    style={{
      marginBottom: 8,
      justifyContent: 'flex-start',
      ...isSelected ? { border: '1px solid' } : {},
    }}
  >
    {children}
  </SelectButton>
);

const LanguageSelectButton = ({ onClick }: ButtonProps) => {
  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  const theme = useTheme();

  if (width <= 600) {
    return (
      <IconButton
        onClick={onClick}
        style={{ backgroundColor: theme.palette.grey[300], borderRadius: 8, padding: '8px 12px' }}
      >
        <img src={LanguageSvg} alt="Language" />
      </IconButton>
    );
  }

  return (
    <SelectButton
      onClick={onClick}
      startIcon={<img src={LanguageSvg} alt="Language" />}
    >
      {t('language_selector.button_text')}
    </SelectButton>
  );
};

export const LanguageSelector: React.FC<{ iconOnly?: boolean }> = ({ iconOnly = false }) => {
  const { t, i18n } = useTranslation();
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const theme = useTheme();
  const changeLanguage = (language: string) => {
    const value = language || EN;
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
  };

  return (
    <Box sx={{ position: 'absolute', right: '20px' }}>
      {iconOnly ? (
        <IconButton
          aria-label={t('language_selector.button_text')}
          onClick={() => setPopoverOpen(true)}
          style={{
            backgroundColor: theme.palette.grey[300],
            borderRadius: 8,
            padding: '8px 12px',
            marginTop: -4,
          }}
        >
          <img src={LanguageSvg} alt="Language" />
        </IconButton>
      ) : (
        <LanguageSelectButton onClick={() => setPopoverOpen(true)} />
      )}
      <Popover open={popoverOpen} onClose={() => setPopoverOpen(false)}>
        <Box>
          <Typography variant="h2" style={{ marginBottom: 16 }}>
            {t('language_selector.modal_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('language_selector.modal_description')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              width: '100%',
              justifyItems: 'stretch',
              py: 3,
            }}
          >
            <LanguageOptionButton
              data-testid="en"
              isSelected={i18n.language === EN}
              onClick={() => changeLanguage(EN)}
            >
              {t('language_selector.choice_english')}
            </LanguageOptionButton>
            <LanguageOptionButton
              data-testid="es"
              isSelected={i18n.language === ES}
              onClick={() => changeLanguage(ES)}
            >
              {t('language_selector.choice_spanish')}
            </LanguageOptionButton>
          </Box>
        </Box>
        <Button
          onClick={() => setPopoverOpen(false)}
          fullWidth
          variant="contained"
          color="primary"
          data-testid="langSubmit"
        >
          {t('language_selector.confirm')}
        </Button>
      </Popover>
    </Box>
  );
};
